// src/Dashboard/pages/teacher/PrepaPhysique/components/AssignmentDataTable.jsx

import React from "react";
import {
  FaCheck,
  FaPen,
  FaTasks,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";

import { formatDateFR, getAdherentName } from "../utils/prepaPhysiqueUtils";

export default function AssignmentDataTable({
  assignments = [],
  loading,
  deletingId,
  onEdit,
  onDelete,
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
          />
        ))}
      </div>
    );
  }

  if (!assignments.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-800/40">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white text-slate-400 shadow-sm dark:bg-slate-900">
          <FaTasks />
        </div>

        <h3 className="mt-4 text-base font-bold text-slate-900 dark:text-white">
          Aucune affectation trouvée
        </h3>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Modifiez vos filtres ou affectez un exercice à des adhérents.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950 md:block">
        <table className="min-w-[1000px] w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <th className="px-4 py-3">Adhérent</th>
              <th className="px-4 py-3">Exercice</th>
              <th className="px-4 py-3">Programme</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Répétitions</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {assignments.map((assignment) => {
              const key = `${assignment.adherent_id}-${assignment.prepa_physique_id}`;
              const deleting =
                deletingId ===
                `assignment-${assignment.adherent_id}-${assignment.prepa_physique_id}`;

              return (
                <tr
                  key={key}
                  className="bg-white transition hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-900"
                >
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900 dark:text-white">
                      {getAdherentName(assignment?.Adherent)}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                    {assignment?.Exercice?.nom || "—"}
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60">
                      {assignment?.Exercice?.programme || "Sans programme"}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {formatDateFR(assignment?.date_realisation)}
                  </td>

                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {assignment?.repetitions ?? "—"}
                  </td>

                  <td className="px-4 py-3">
                    <StatusPill done={!!assignment?.is_done} />
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <IconButton
                        label="Modifier"
                        icon={<FaPen />}
                        onClick={() => onEdit?.(assignment)}
                        className="bg-indigo-600 text-white hover:bg-indigo-700"
                      />

                      <IconButton
                        label={deleting ? "Suppression..." : "Supprimer"}
                        icon={<FaTrashAlt />}
                        onClick={() => onDelete?.(assignment)}
                        disabled={deleting}
                        className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 md:hidden">
        {assignments.map((assignment) => (
          <article
            key={`${assignment.adherent_id}-${assignment.prepa_physique_id}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate text-sm font-bold text-slate-900 dark:text-white">
                  {getAdherentName(assignment?.Adherent)}
                </h3>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {assignment?.Exercice?.nom || "—"}
                </p>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {formatDateFR(assignment?.date_realisation)} ·{" "}
                  {assignment?.repetitions ?? "—"} répétition(s)
                </p>
              </div>

              <StatusPill done={!!assignment?.is_done} />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onEdit?.(assignment)}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-indigo-600 text-sm font-bold text-white transition hover:bg-indigo-700"
              >
                <FaPen />
                Modifier
              </button>

              <button
                type="button"
                onClick={() => onDelete?.(assignment)}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-red-600 text-sm font-bold text-white transition hover:bg-red-700"
              >
                <FaTrashAlt />
                Supprimer
              </button>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function StatusPill({ done }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
        done
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
          : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60"
      }`}
    >
      {done ? <FaCheck /> : <FaTimes />}
      {done ? "Fait" : "Non fait"}
    </span>
  );
}

function IconButton({ icon, label, onClick, className = "", disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`inline-flex h-10 w-10 items-center justify-center rounded-xl text-sm font-bold transition ${className}`}
    >
      {icon}
    </button>
  );
}